import type { InvalidatedProtocol } from "@repo/domain";
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  isNonJsonSerializable,
  splitLink,
  type TRPCClientError,
  type TRPCLink,
} from "@trpc/client";
import { observable } from "@trpc/server/observable";
import type { AppRouter } from "./routers";
import { syncLink } from "./sync-link";

// Re-export types for convenience
export { isErr, isOk, type WorkflowResult } from "./lib/result";
export type { AppRouter } from "./routers";

let client: ReturnType<typeof createTRPCClient<AppRouter>> | null = null;

/**
 * tRPC error codes that represent user-actionable domain-rule violations — the
 * server's `mapErrorToTRPC` maps `PreconditionFailedError`, `SlotConflictError`,
 * and validation failures to these. Their `.message` is human-meaningful and
 * safe to show directly. Infra/auth codes (500, 401, NOT_FOUND) are excluded.
 */
const DOMAIN_ERROR_CODES = new Set<string>(["PRECONDITION_FAILED", "CONFLICT", "BAD_REQUEST"]);

/** True for tRPC errors carrying a user-meaningful domain-rule message. */
export function isDomainError(err: unknown): err is TRPCClientError<AppRouter> {
  const code = (err as TRPCClientError<AppRouter> | null | undefined)?.data?.code;
  return typeof code === "string" && DOMAIN_ERROR_CODES.has(code);
}

/** The protocols a refused catalog save would have invalidated, when that is why it was refused. */
export function invalidatedProtocolsOf(err: unknown): readonly InvalidatedProtocol[] | undefined {
  const domain = (err as TRPCClientError<AppRouter> | null | undefined)?.data?.domain;
  return domain?.code === "CATALOG_PROTOCOLS_INVALIDATED"
    ? (domain.details as { protocols: readonly InvalidatedProtocol[] }).protocols
    : undefined;
}

/** Zod's default tRPC message serializes its issue array, including paths and regex details. */
export function isValidationPayload(message: string): boolean {
  try {
    const issues: unknown = JSON.parse(message);
    return (
      Array.isArray(issues) &&
      issues.length > 0 &&
      issues.every(
        (issue) =>
          issue !== null &&
          typeof issue === "object" &&
          typeof issue.code === "string" &&
          typeof issue.message === "string" &&
          Array.isArray(issue.path),
      )
    );
  } catch {
    return false;
  }
}

/** Link that taps every operation's error so the app can surface it once, globally. */
function errorObservingLink(onError: (err: TRPCClientError<AppRouter>) => void): TRPCLink<AppRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) =>
        next(op).subscribe({
          next: (value) => observer.next(value),
          error: (err) => {
            if (!op.context.writeErrorHandled) onError(err);
            observer.error(err);
          },
          complete: () => observer.complete(),
        }),
      );
}

/** Initialize the tRPC client. Call once at app startup. */
export function initTRPCClient(options: {
  url: string;
  /**
   * Resolve the bearer token for each request. May be async so callers can
   * refresh an expired token before it's read — `getAccessToken` on the Medplum
   * client is a pure getter and won't refresh on its own, so a token that
   * expired while the tab sat idle would otherwise 500 the request.
   */
  getAccessToken: () => string | null | Promise<string | null>;
  /** Called for every failed operation, before the error propagates to the caller. */
  onError?: (err: TRPCClientError<AppRouter>) => void;
}) {
  const headers = async () => {
    const token = await options.getAccessToken();
    return token ? { authorization: `Bearer ${token}` } : {};
  };
  // A FormData or binary body cannot be batched into a JSON array, so those
  // operations take a link of their own. Everything else still batches.
  const terminalLink = splitLink({
    condition: (op) => isNonJsonSerializable(op.input),
    true: httpLink({ url: options.url, headers }),
    false: httpBatchLink({ url: options.url, headers }),
  });
  client = createTRPCClient<AppRouter>({
    links: [...(options.onError ? [errorObservingLink(options.onError)] : []), syncLink(), terminalLink],
  });
  return client;
}

/** Get the tRPC client. Must call initTRPCClient first. */
export function getTRPCClient() {
  if (!client) {
    throw new Error("Call initTRPCClient before using getTRPCClient");
  }
  return client;
}
